// Common types for the application

// Store types
export interface Store {
  id?: string; // Used for UI components
  storeNumber: string;
  franchiseCode?: string;
  menus?: {
    edges?: Array<{
      node: {
        salesChannels: string[];
        menuId: string;
        createdAt: string;
      }
    }> | null;
  };
  assignedMenuDefinitions?: Array<{
    menuDefinition: {
      menuId: string;
      name: string;
    };
    patches: Array<{
      patchId: string;
      name: string;
    }>;
  }> | null;
  compressedMenuByChannel?: {
    url: string;
    expiry: string;
  };
  // Additional fields for UI display
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  isActive?: boolean;
}

export interface StoreResponse {
  count: number;
  page: number;
  pages: number;
  stores: Store[];
}

export interface StoreNode {
  storeNumber: string;
  franchiseCode: string;
  menus: {
    edges: null;
  };
  assignedMenuDefinitions: null;
  compressedMenuByChannel: {
    url: string;
    expiry: string;
  };
}

export interface FranchiseGroup {
  id: string;
  name: string;
  storeCount: number;
  stores?: {
    totalCount: number;
    edges: Array<{
      node: StoreNode;
    }>;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface FranchiseGroupResponse {
  count: number;
  page: number;
  pages: number;
  storeGroups: FranchiseGroup[];
}

// Patch types
export enum PatchType {
  PRODUCT_PRICE = '_SetProdPrices',
  PRODUCT_EXCLUSION = '_RmvProdVar',
  BUNDLE_PRICE = '_SetBundPrice',
  BUNDLE_EXCLUSION = '_RmvBund',
}

export interface Patch {
  id: string;
  storeNumbers: string[];
  patchType: PatchType;
  createdAt: string;
  updatedAt: string;
  status: 'DRAFT' | 'PUBLISHED' | 'SCHEDULED';
  scheduledDate?: string;
  createdBy: string;
}

export interface ProductPricePatch extends Patch {
  patchType: PatchType.PRODUCT_PRICE;
  productId: string;
  price: number;
  originalPrice?: number;
}

export interface ProductExclusionPatch extends Patch {
  patchType: PatchType.PRODUCT_EXCLUSION;
  productId: string;
  isExcluded: boolean;
}

export interface BundlePricePatch extends Patch {
  patchType: PatchType.BUNDLE_PRICE;
  bundleId: string;
  price: number;
  originalPrice?: number;
}

export interface BundleExclusionPatch extends Patch {
  patchType: PatchType.BUNDLE_EXCLUSION;
  bundleId: string;
  isExcluded: boolean;
}

// Product types
export interface Product {
  id: string;
  name: string;
  description: string;
  basePrice: number;
  category: string;
  imageUrl?: string;
  isActive: boolean;
}

// Bundle types
export interface Bundle {
  id: string;
  name: string;
  description: string;
  basePrice: number;
  products: string[];
  imageUrl?: string;
  isActive: boolean;
}

// API response types
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
